import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import EmailFolder from "@mapmycustomers/shared/enum/EmailFolder";

import {
  searchParentEmailThreads,
  setParentEmailThreadsFolder,
} from "@app/component/EntityEmails/store/actions";
import { getParentEmailThreadsFolder } from "@app/component/EntityEmails/store/selectors";
import RadioGroup from "@app/component/input/RadioGroup";
import { isNylasConnectionActive } from "@app/store/nylas";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import getEmailFolderAnalyticsName from "../util/getEmailFolderAnalyticsName";

import messages from "./messages";

interface ParentEmailsFolderProps {
  folder: EmailFolder;
  nylasActive: boolean;
  onFolderChange: typeof setParentEmailThreadsFolder;
  onSearch: typeof searchParentEmailThreads.request;
}

const ParentEmailsFolder: React.FC<ParentEmailsFolderProps> = ({
  folder,
  nylasActive,
  onFolderChange,
  onSearch,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const folderOptions = useMemo(
    () => [
      { label: intl.formatMessage(messages.inboxFolderLabel), value: EmailFolder.INBOX },
      { label: intl.formatMessage(messages.sentFolderLabel), value: EmailFolder.SENT },
    ],
    [intl]
  );

  const handleFolderChange = useCallback(
    (folder: EmailFolder) => {
      onFolderChange(folder);

      if (nylasActive) {
        onSearch();
      }

      analytics.clicked([getEmailFolderAnalyticsName(folder)]);
    },
    [analytics, nylasActive, onFolderChange, onSearch]
  );

  return (
    <RadioGroup
      onChange={handleFolderChange}
      options={folderOptions}
      optionType="button"
      size="small"
      value={folder}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  folder: getParentEmailThreadsFolder(state),
  nylasActive: isNylasConnectionActive(state),
});

const mapDispatchToProps = {
  onFolderChange: setParentEmailThreadsFolder,
  onSearch: searchParentEmailThreads.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ParentEmailsFolder);
